import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScrollText, Plus, Pencil, Trash2, ShieldAlert } from "lucide-react";
import { disciplineApi, sportsApi, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, SectionTitle, EmptyState } from "@/components/shared";
import { Reveal, SkeletonList } from "@/components/motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SANCTION_LABEL = { avertissement: "Avertissement", suspension: "Suspension temporaire", bannissement: "Bannissement" };
const SANCTION_CLS = { avertissement: "bg-amber-50 text-amber-800 border-amber-200", suspension: "bg-rose-50 text-rose-800 border-rose-200", bannissement: "bg-red-50 text-red-800 border-red-200" };

export default function RulesPage() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [sports, setSports] = useState([]);
  const [sanctions, setSanctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", sport_id: "general" });
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([disciplineApi.rules(), sportsApi.list()]);
      setRules(r.data); setSports(s.data);
      if (user) { try { const sa = await disciplineApi.sanctions(); setSanctions(sa.data); } catch {} }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user]);

  const openNew = () => { setEditing(null); setForm({ title: "", content: "", sport_id: "general" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ title: r.title, content: r.content, sport_id: r.sport_id || "general" }); setOpen(true); };
  const save = async () => {
    if (!form.title || !form.content) { toast.error("Titre et contenu requis"); return; }
    setBusy(true);
    const payload = { title: form.title, content: form.content, sport_id: form.sport_id === "general" ? null : form.sport_id };
    try {
      if (editing) await disciplineApi.updateRule(editing.id, payload); else await disciplineApi.createRule(payload);
      toast.success("Enregistré"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await disciplineApi.removeRule(id); toast.success("Supprimé"); load(); } catch (e) { toast.error(errMsg(e)); } };

  const general = rules.filter((r) => !r.sport_id);
  const bySport = sports.map((s) => ({ sport: s, docs: rules.filter((r) => r.sport_id === s.id) })).filter((g) => g.docs.length);

  return (
    <PageContainer>
      <SectionTitle action={isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" onClick={openNew} data-testid="reglement-creer-bouton"><Plus className="mr-1.5 h-4 w-4" />Document</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouveau"} document</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Titre *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reglement-titre-input" /></div>
              <div className="space-y-1.5"><Label>Portée</Label><Select value={form.sport_id} onValueChange={(v) => setForm({ ...form, sport_id: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">Général</SelectItem>{sports.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Contenu *</Label><Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="reglement-contenu-input" /></div>
            </div>
            <DialogFooter><Button onClick={save} disabled={busy} data-testid="reglement-enregistrer-bouton">{busy ? "…" : "Enregistrer"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}>Règlements et charte</SectionTitle>

      {user && sanctions.length > 0 && (
        <Reveal><Card className="border-rose-200 bg-rose-50/50"><CardContent className="p-4">
          <div className="mb-2 flex items-center gap-2 font-heading font-semibold text-rose-800"><ShieldAlert className="h-4 w-4" />{isAdmin ? "Sanctions (aperçu)" : "Mes sanctions"}</div>
          <div className="space-y-2">{sanctions.slice(0, 5).map((s) => (
            <div key={s.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${SANCTION_CLS[s.type]}`}>
              <span><b>{SANCTION_LABEL[s.type]}</b>{isAdmin ? ` — ${s.member_name}` : ""} : {s.reason}</span>
              <span className="text-xs">{s.duration_days ? `${s.duration_days} j` : ""} {s.active ? "" : "(levée)"}</span>
            </div>
          ))}</div>
        </CardContent></Card></Reveal>
      )}

      {loading ? <SkeletonList count={4} /> : (
        <div className="space-y-6">
          <Reveal>
            <h3 className="mb-2 font-heading font-semibold">Général</h3>
            {general.length === 0 ? <EmptyState title="Aucun document général" icon={ScrollText} /> : (
              <Accordion type="single" collapsible className="rounded-2xl border bg-card px-4">
                {general.map((r) => (
                  <AccordionItem key={r.id} value={r.id}>
                    <AccordionTrigger className="text-left" data-testid="reglement-item"><span className="font-medium">{r.title}</span></AccordionTrigger>
                    <AccordionContent><p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.content}</p><div className="mt-2 text-[11px] text-muted-foreground">Mis à jour le {r.updated_at?.slice(0, 10)}</div>
                      {isAdmin && <div className="mt-3 flex gap-2"><Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="mr-1.5 h-3.5 w-3.5" />Modifier</Button><Button size="sm" variant="outline" className="text-rose-600" onClick={() => remove(r.id)}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Supprimer</Button></div>}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </Reveal>
          {bySport.map((g, gi) => (
            <Reveal key={g.sport.id} delay={0.05 * (gi + 1)}>
              <h3 className="mb-2 font-heading font-semibold">{g.sport.name}</h3>
              <Accordion type="single" collapsible className="rounded-2xl border bg-card px-4">
                {g.docs.map((r) => (
                  <AccordionItem key={r.id} value={r.id}>
                    <AccordionTrigger className="text-left"><span className="font-medium">{r.title}</span></AccordionTrigger>
                    <AccordionContent><p className="whitespace-pre-wrap text-sm text-muted-foreground">{r.content}</p>
                      {isAdmin && <div className="mt-3 flex gap-2"><Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="mr-1.5 h-3.5 w-3.5" />Modifier</Button><Button size="sm" variant="outline" className="text-rose-600" onClick={() => remove(r.id)}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Supprimer</Button></div>}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
